import { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { SellerFinancingHero } from './components/SellerFinancingHero';
import { WhatIsSellerFinancing } from './components/WhatIsSellerFinancing';
import { ConsistentIncomeStream } from './components/ConsistentIncomeStream';
import { FasterClosingProcess } from './components/FasterClosingProcess';
import { HigherSalePrice } from './components/HigherSalePrice';
import { TaxAdvantagesInstallment } from './components/TaxAdvantagesInstallment';
import { ExpandedBuyerPool } from './components/ExpandedBuyerPool';
import { SaleAsIsLowerCosts } from './components/SaleAsIsLowerCosts';
import { IncreasedSecurityControl } from './components/IncreasedSecurityControl';
import { SellerFinancingStructures } from './components/SellerFinancingStructures';
import { RisksAndMitigation } from './components/RisksAndMitigation';
import { QualifyingBuyers } from './components/QualifyingBuyers';
import { LegalDocumentation } from './components/LegalDocumentation';
import { CaseStudiesExamples } from './components/CaseStudiesExamples';
import { TaxPlanningStrategies } from './components/TaxPlanningStrategies';
import { ComparingFinancingOptions } from './components/ComparingFinancingOptions';
import { SellerFinancingFAQ } from './components/SellerFinancingFAQ';
import { SellerFinancingFinalCTA } from './components/SellerFinancingFinalCTA';

export default function SellerFinancingBenefits() {
  useEffect(() => {
    // Set document title
    document.title = 'Seller Financing Benefits: Tax Advantages &amp; Income Strategies for Apartment Building Sales';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover the top 7 tax benefits and advantages of seller financing for apartment buildings. Learn how to generate passive income, defer capital gains taxes, achieve higher sale prices, and expand your buyer pool with installment sales strategies.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Discover the top 7 tax benefits and advantages of seller financing for apartment buildings. Learn how to generate passive income, defer capital gains taxes, achieve higher sale prices, and expand your buyer pool with installment sales strategies.';
      document.head.appendChild(meta);
    }

    // Set meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'seller financing benefits, seller financing tax advantages, installment sale tax benefits, apartment building seller financing, owner financing advantages, capital gains tax deferral, passive income real estate, seller carry back financing');
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      metaKeywords.setAttribute('content', 'seller financing benefits, seller financing tax advantages, installment sale tax benefits, apartment building seller financing, owner financing advantages, capital gains tax deferral, passive income real estate, seller carry back financing');
      document.head.appendChild(metaKeywords);
    }

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://sellapartmentscash.com/seller-financing-benefits');
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', 'https://sellapartmentscash.com/seller-financing-benefits');
      document.head.appendChild(canonical);
    }

    // Add structured data for Article with FAQ
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Seller Financing Benefits: Tax Advantages &amp; Income Strategies for Apartment Building Sales",
      "description": "Comprehensive guide to the tax benefits and financial advantages of seller financing for apartment building sales, including installment sale strategies and capital gains tax deferral.",
      "author": {
        "@type": "Organization",
        "name": "Sell Apartments Cash"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Sell Apartments Cash",
        "logo": {
          "@type": "ImageObject",
          "url": "https://sellapartmentscash.com/logo.png"
        }
      },
      "datePublished": "2024-01-15",
      "dateModified": new Date().toISOString().split('T')[0],
      "mainEntity": {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What are the main tax benefits of seller financing for apartment buildings?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The primary tax benefit is the ability to defer capital gains taxes through an installment sale. Instead of paying all taxes in one year, you spread the tax liability over the life of the loan as you receive payments. This can significantly reduce your annual tax burden and keep you in a lower tax bracket."
            }
          },
          {
            "@type": "Question",
            "name": "How does seller financing generate passive income?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "With seller financing, you receive monthly payments that include both principal and interest, typically at rates of 6-10% or higher. This creates a steady stream of passive income that can exceed returns from traditional investments, providing consistent cash flow for years or decades."
            }
          },
          {
            "@type": "Question",
            "name": "Can I charge a higher price with seller financing?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, sellers offering financing can typically command 10-20% higher sale prices because they provide a valuable service to buyers who cannot obtain traditional bank financing. The convenience and accessibility of seller financing justifies the premium pricing."
            }
          },
          {
            "@type": "Question",
            "name": "What happens if the buyer defaults on seller financing?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "If a buyer defaults, you retain the legal right to foreclose and take back the property. You keep all payments received to date, regain ownership of the property, and can resell it again. This provides significant security compared to a traditional sale."
            }
          },
          {
            "@type": "Question",
            "name": "Do I need to consult a tax professional about seller financing?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolutely. Every tax situation is different, and the benefits of seller financing depend on your specific circumstances, including your tax bracket, property basis, depreciation recapture, and overall financial goals. Always consult with a qualified tax advisor or CPA before structuring a seller-financed transaction."
            }
          }
        ]
      }
    };

    // Add structured data script
    let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else {
      structuredDataScript = document.createElement('script');
      structuredDataScript.type = 'application/ld+json';
      structuredDataScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataScript);
    }

    // Cleanup function
    return () => {
      const elementsToRemove = [
        document.querySelector('meta[name="description"]'),
        document.querySelector('meta[name="keywords"]'),
        document.querySelector('link[rel="canonical"]'),
        document.querySelector('script[type="application/ld+json"]')
      ].filter(element => {
        if (!element) return false;
        const content = element.getAttribute('content') || element.getAttribute('href') || element.textContent || '';
        return content.includes('seller financing') || content.includes('seller-financing-benefits');
      });
      
      elementsToRemove.forEach(element => element?.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SellerFinancingHero />
      <WhatIsSellerFinancing />
      <ConsistentIncomeStream />
      <FasterClosingProcess />
      <HigherSalePrice />
      <TaxAdvantagesInstallment />
      <ExpandedBuyerPool />
      <SaleAsIsLowerCosts />
      <IncreasedSecurityControl />
      <SellerFinancingStructures />
      <RisksAndMitigation />
      <QualifyingBuyers />
      <LegalDocumentation />
      <CaseStudiesExamples />
      <TaxPlanningStrategies />
      <ComparingFinancingOptions />
      <SellerFinancingFAQ />
      <SellerFinancingFinalCTA />
      <Footer />
    </div>
  );
}